import { RfxTab, RfxTabTotals } from "newui/features/Rfx/types/component-types";
import { useMemo } from "react";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { RFX_TABS } from "utils/constants";
import { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import { CLARIFICATION_VISIBLE_STATUSES } from "newui/features/Rfx/Clarifications";
import { EVALUATION_VISIBLE_STATUSES } from "newui/features/Rfx/Evaluation";
import { useHasClarificationsAccess, useHasEvaluationAccess } from "hooks/featureFlags";

export interface RfxTabMenuItem extends TabMenuItem {
	id: RfxTab;
	hidden?: boolean;
	sortOrder: number;
}

export const getTabCounts = (
	rfx: Rfx,
	questionnaireLength?: number,
	quoteLength?: number,
	clarificationsCount?: number
): RfxTabTotals => {
	return {
		addendums: rfx?.addendum_count ?? 0,
		clarifications: clarificationsCount ?? 0,
		comms: rfx?.comms_count ?? 0,
		documentation: rfx?.documentationDocument?.length ?? 0,
		evaluation: 0,
		returnableSchedule:
			rfx?.returnableScheduleType === "file" ? 1 : (questionnaireLength ?? 0),
		quote: rfx?.quoteType === "file" ? 1 : (rfx?.quotes?.length ?? 0),
	} as RfxTabTotals;
};

interface UseRfxTabMenuOptions {
	isClientView?: boolean;
	/**
	 * PRO-637: viewer is an external evaluation panel member — in neither the
	 * procuring company nor the responding supplier. They read the submission in
	 * order to score it, but have no direct channel to either party, so Comms is
	 * hidden for them.
	 */
	isPanelViewer?: boolean;
}

export const useRfxTabMenu = (
	rfx: Rfx,
	tabCounts: RfxTabTotals,
	options: UseRfxTabMenuOptions = {}
) => {
	const isClarificationsEnabled = useHasClarificationsAccess();
	const hasEvaluationAccess = useHasEvaluationAccess();

	const { isClientView = false, isPanelViewer = false } = options;
	const showQuote = rfx?.rfxType?.show_quote ?? true;

	// For clients: show when status is EVALUATION or later
	// For suppliers: show when they have clarifications
	// PRO-637: clarifications are run BY the procuring side. A panel member scores
	// submissions; they do not raise or answer clarifications, so the tab is hidden
	// for them. The RFx Lead is unaffected — they reach this view through the
	// owning company, not as an external panellist, so `isPanelViewer` is false.
	const showClarifications =
		isClarificationsEnabled &&
		!isPanelViewer &&
		(isClientView
			? CLARIFICATION_VISIBLE_STATUSES.includes(
					rfx?.status?.toUpperCase() as (typeof CLARIFICATION_VISIBLE_STATUSES)[number]
				)
			: tabCounts.clarifications > 0);

	const showEvaluation =
		isClientView &&
		hasEvaluationAccess &&
		EVALUATION_VISIBLE_STATUSES.includes(
			rfx?.status?.toUpperCase() as (typeof EVALUATION_VISIBLE_STATUSES)[number]
		);

	const tabMenu = useMemo(() => {
		const tabs: RfxTabMenuItem[] = [
			{
				id: RFX_TABS.DOCUMENTATION,
				name: "Documentation",
				showBadge: true,
				total: tabCounts.documentation,
				isValid: true,
				sortOrder: 1,
			},
			{
				id: RFX_TABS.QUESTIONNAIRE,
				name: "Returnable Schedules",
				showBadge: true,
				total: tabCounts.returnableSchedule,
				isValid: false,
				sortOrder: 2,
			},
			{
				id: RFX_TABS.QUOTE,
				name: "Quote",
				showBadge: true,
				total: tabCounts.quote,
				isValid: false,
				sortOrder: 3,
				disabled: !showQuote,
			},
			{
				id: RFX_TABS.COMMS,
				name: "Comms",
				showBadge: true,
				total: tabCounts.comms,
				isValid: true,
				sortOrder: 4,
				// PRO-637: Comms is a direct procurer<->supplier channel. An external
				// panel member is party to neither side and must not communicate with
				// either, so the tab is hidden for them.
				hidden: isPanelViewer,
			},
			{
				id: RFX_TABS.ADDENDUMS,
				name: "Addendums",
				showBadge: true,
				total: tabCounts.addendums,
				isValid: true,
				sortOrder: 5,
			},
			{
				id: RFX_TABS.CLARIFICATIONS,
				name: "Clarifications",
				showBadge: !isClientView,
				total: tabCounts.clarifications,
				isValid: true,
				sortOrder: 6,
				hidden: !showClarifications,
			},
			{
				id: RFX_TABS.EVALUATION,
				name: "Evaluation",
				showBadge: false,
				total: 0,
				isValid: true,
				sortOrder: 7,
				hidden: !showEvaluation,
			},
		];

		return tabs
			.filter((tab) => !tab.hidden)
			.sort((a, b) => a.sortOrder - b.sortOrder);
	}, [isClientView, isPanelViewer, showQuote, tabCounts, showClarifications, showEvaluation]);

	return { tabMenu };
};
